/* Selected-variant coupon UI only; checkout prices and discounts stay in Shopify. */
if (!customElements.get('ka-pdp-pricing')) {
  class KaPdpPricing extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-coupon-panel]');
      if (!this.panel) return;
      this.endTime = Number(this.panel.dataset.expiresAt) * 1000;
      if (Number.isFinite(this.endTime)) this.updateEndDate();
      this.copyButton = this.querySelector('[data-coupon-copy]');
      this.copyHandler = () => this.copyCode();
      this.copyButton?.addEventListener('click', this.copyHandler);
      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.copyTimer);
      this.copyButton?.removeEventListener('click', this.copyHandler);
    }

    updateEndDate() {
      const target = this.querySelector('[data-coupon-end-date]');
      if (!target) return;
      const parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Brisbane', weekday: 'short', day: 'numeric', month: 'short',
        hour: 'numeric', minute: '2-digit', hour12: true
      }).formatToParts(new Date(this.endTime));
      const value = type => parts.find(part => part.type === type)?.value || '';
      target.textContent = `${value('weekday')}, ${value('day')} ${value('month')}, ${value('hour')}:${value('minute')}${value('dayPeriod').toLowerCase()} AEST`;
    }

    tick() {
      clearTimeout(this.timer);
      const remaining = this.endTime - Date.now();
      const active = Number.isFinite(remaining) && remaining > 0;
      this.panel.hidden = !active;
      this.querySelectorAll('[data-coupon-fallback]').forEach(element => { element.hidden = active; });
      const payment = this.querySelector('[data-coupon-payment]');
      const paymentAmount = payment?.querySelector('[data-coupon-payment-amount]');
      if (paymentAmount) paymentAmount.textContent = active ? payment.dataset.couponAmount : (payment.dataset.regularAmount || '');
      if (payment) payment.hidden = (active && this.panel.dataset.couponFree === 'true') || (!active && payment.hasAttribute('data-coupon-active-only'));
      if (this.copyButton) this.copyButton.disabled = !active;
      if (!active) return;

      const seconds = Math.floor(remaining / 1000);
      const values = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds % 86400 / 3600), minutes: Math.floor(seconds % 3600 / 60), seconds: seconds % 60 };
      for (const [unit, value] of Object.entries(values)) {
        const target = this.querySelector(`[data-coupon-${unit}]`);
        if (target) target.textContent = String(value).padStart(2, '0');
      }
      this.querySelector('[data-coupon-timer]')?.setAttribute('aria-label', `${values.days} days, ${values.hours} hours, ${values.minutes} minutes and ${values.seconds} seconds remaining`);
      const interval = this.querySelector('[data-coupon-timer]') ? 1000 - Date.now() % 1000 + 10 : 60000;
      this.timer = setTimeout(() => this.tick(), Math.min(interval, remaining));
    }

    async copyCode() {
      if (!Number.isFinite(this.endTime) || this.endTime <= Date.now()) { this.tick(); return; }
      const code = this.panel.dataset.couponCode;
      const label = this.copyButton.querySelector('[data-coupon-copy-label]');
      const status = this.querySelector('[data-coupon-status]');
      let copied = false;
      try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(code); copied = true; }
      } catch (_) { /* Use the selection fallback when clipboard permission is unavailable. */ }
      if (!copied) {
        const input = document.createElement('textarea');
        input.value = code;
        input.style.cssText = 'position:fixed;left:-9999px;top:0';
        input.setAttribute('readonly', '');
        document.body.appendChild(input);
        try { input.select(); copied = document.execCommand('copy'); } catch (_) { copied = false; }
        finally { input.remove(); this.copyButton.focus({ preventScroll: true }); }
      }
      if (!this.isConnected) return;
      label.textContent = copied ? 'Copied' : 'Try again';
      status.textContent = copied ? 'Discount code copied. Apply it at checkout.' : 'Could not copy. Select the displayed code and copy it manually.';
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { label.textContent = 'Copy'; status.textContent = ''; }, 2500);
    }
  }
  customElements.define('ka-pdp-pricing', KaPdpPricing);
}
